import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Contact";
import { PageHero } from "@/components/site/PageHero";

const CATEGORIES = ["All", "LaunchLab", "Startups", "Registrations", "Certifications", "Technology", "Projects", "Pricing", "Careers", "General"];

export default function Faq() {
  const [faqs, setFaqs] = useState(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "FAQ | LaunchLab";
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => setFaqs([]));
  }, []);

  const filtered = useMemo(() => {
    if (!faqs) return [];
    return faqs.filter((f) => {
      const matchCat = category === "All" || f.category === category;
      const q = query.trim().toLowerCase();
      const matchQ = !q || `${f.question} ${f.answer}`.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [faqs, query, category]);

  return (
    <div data-testid="faq-page" className="relative w-full max-w-[100vw] overflow-x-clip text-slate-100 min-h-screen">
      <Header />
      <PageHero
        testid="faq-hero"
        kicker="Questions & Answers"
        title="EVERYTHING YOU"
        highlight="NEED TO KNOW."
        subtext="Answers about LaunchLab, venture building, registrations, pricing and careers."
      />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="faq-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search questions..."
            className="w-full glass rounded-full pl-11 pr-4 py-3.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-[#FF8800]"
          />
        </div>
        <div className="flex flex-wrap gap-2 mb-10" data-testid="faq-categories">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`faq-category-${c.toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${
                category === c ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]" : "glass text-slate-300 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {faqs === null ? (
          <div className="flex justify-center py-16"><div className="w-10 h-10 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center text-slate-400" data-testid="faq-empty">No questions match your search.</div>
        ) : (
          <div className="space-y-3" data-testid="faq-list">
            {filtered.map((f, i) => {
              const isOpen = open === f.id;
              return (
                <motion.div
                  key={f.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-20px" }}
                  transition={{ duration: 0.4, delay: i * 0.03 }}
                  className={`glass rounded-xl overflow-hidden transition-colors ${isOpen ? "border-[#FF8800]/40" : ""}`}
                  data-testid={`faq-item-${f.id}`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? null : f.id)}
                    data-testid={`faq-toggle-${f.id}`}
                    aria-expanded={isOpen}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left group focus:outline-none"
                  >
                    <div className="flex items-center gap-3.5">
                      <span className="font-mono-tech text-[9px] tracking-[0.18em] px-2.5 py-1 rounded-full border border-[#00B0FF]/40 text-[#00B0FF] uppercase shrink-0 hidden sm:block">
                        {f.category}
                      </span>
                      <span className="font-display font-semibold text-white text-sm sm:text-base">{f.question}</span>
                    </div>
                    <ChevronDown className={`w-4 h-4 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180 text-[#FF8800]" : "text-slate-500"}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 pb-6 text-sm sm:text-base text-slate-300 leading-relaxed" data-testid={`faq-answer-${f.id}`}>
                          {f.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
